import { z } from 'zod';
import { LeadSourceType } from '../../leads/Lead.js';
import { personalityProfileSchema } from './AiBdcSettings.js';
import { serviceConfigSchema } from './SharedServiceConfig.js';

// --- Follow-up sequence entry ---
const followUpEntrySchema = z.object({
    delayValue: z.coerce.number().min(1).max(168),
    delayUnit: z.enum(['hours', 'days']),
});
// --- Base sub-schemas (shape only — no .optional()/.default() baked in) ---
// Usage sites apply .default() (main config) or .optional() (DTO partial updates).
/** Single appointment type config */
const appointmentTypeConfigSchema = z.object({
    enabled: z.boolean().default(false),
    calendarProvider: z.string().optional(),
    calendarId: z.string().optional(),
    defaultLength: z.number().min(5).max(240).default(30),
    notificationEmail: z.string().email().optional(),
});
/** Simple skill toggle */
const skillToggleSchema = z.object({
    enabled: z.boolean().default(false),
});
/** Automated follow-up settings */
const followUpsSchema = z.object({
    enabled: z.boolean().default(false),
    sequence: z
        .array(followUpEntrySchema)
        .min(1)
        .max(3)
        .default([{ delayValue: 24, delayUnit: 'hours' }]),
});
/** Escalation skill — toggle + optional email */
const escalationSkillSchema = z.object({
    enabled: z.boolean().default(false),
    email: z.string().email().optional(),
});
/** SMS settings */
const smsSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    autoResponseDelay: z.number().optional(),
    twilioPhone: z.coerce.string().optional(),
    // Not actively used today. In the future, a Twilio Messaging Service SID enables
    // 10DLC campaign compliance, intelligent number-pool routing, and higher
    // deliverability — useful when an SVP operates multiple phone numbers.
    twilioMessagingServiceSid: z.coerce.string().optional(),
    twilioAccountSid: z.coerce.string().optional(), // per-SVP subaccount SID
    twilioAuthToken: z.coerce.string().optional(), // encrypted; decrypted at runtime via decryptField()
});
/** Quick action pill shown in the chat widget */
const quickActionPillSchema = z.object({
    title: z.string(),
    prompt: z.string(),
});
/** Embedded website assistant (chat widget) settings */
const embeddedWebsiteAssistantSchema = z.object({
    enabled: z.boolean().optional(),
    suggestions: z.array(z.string()).optional(),
    inputPlaceholders: z.array(z.string()).optional(),
    brandColor: z.string().optional(),
    darkMode: z.boolean().optional(),
    fontFamily: z.string().optional(),
    quickActionPills: z.array(quickActionPillSchema).optional(),
    showHelpButton: z.boolean().optional(),
    enableTextOffRamp: z.boolean().optional(),
});
// --- Main config schemas (all sub-fields required with defaults) ---
// These are used in bdcServiceConfigSchema to ensure fully-populated records.
const DEFAULT_APPOINTMENT = { enabled: false, defaultLength: 30 };
const salesBookingsSchema = z.object({
    testDrive: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    showroomVisit: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    salesConsultation: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    tradeInAppraisal: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
});
const serviceBookingsSchema = z.object({
    routineMaintenance: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    diagnosticInspection: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    recall: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
    bodyWork: appointmentTypeConfigSchema.default(DEFAULT_APPOINTMENT),
});
const bookingsSchema = z.object({
    sales: salesBookingsSchema.default({}),
    service: serviceBookingsSchema.default({}),
});
const skillsSchema = z.object({
    followUps: followUpsSchema.default({
        enabled: false,
        sequence: [{ delayValue: 24, delayUnit: 'hours' }],
    }),
    prequalification: skillToggleSchema.default({ enabled: false }),
    tradeInValuation: skillToggleSchema.default({ enabled: false }),
    checkout: skillToggleSchema.default({ enabled: false }),
    preorder: skillToggleSchema.default({ enabled: false }),
    shippingFeeEstimate: skillToggleSchema.default({ enabled: false }),
    escalation: escalationSkillSchema.default({ enabled: false }),
    bookings: bookingsSchema.default({}),
});
// --- DTO schemas (all optional — for partial updates; must not trigger defaults) ---
// Sending { skills: { followUps: {...} } } must not populate or overwrite other skills.
const salesBookingsDtoSchema = z
    .object({
    testDrive: appointmentTypeConfigSchema.optional(),
    showroomVisit: appointmentTypeConfigSchema.optional(),
    salesConsultation: appointmentTypeConfigSchema.optional(),
    tradeInAppraisal: appointmentTypeConfigSchema.optional(),
})
    .optional();
const serviceBookingsDtoSchema = z
    .object({
    routineMaintenance: appointmentTypeConfigSchema.optional(),
    diagnosticInspection: appointmentTypeConfigSchema.optional(),
    recall: appointmentTypeConfigSchema.optional(),
    bodyWork: appointmentTypeConfigSchema.optional(),
})
    .optional();
const bookingsDtoSchema = z
    .object({
    sales: salesBookingsDtoSchema,
    service: serviceBookingsDtoSchema,
})
    .optional();
const skillsDtoSchema = z
    .object({
    followUps: followUpsSchema.optional(),
    prequalification: skillToggleSchema.optional(),
    tradeInValuation: skillToggleSchema.optional(),
    checkout: skillToggleSchema.optional(),
    preorder: skillToggleSchema.optional(),
    shippingFeeEstimate: skillToggleSchema.optional(),
    escalation: escalationSkillSchema.optional(),
    bookings: bookingsDtoSchema,
})
    .optional();
// --- Main BdcServiceConfig schema ---
const bdcServiceConfigSchema = serviceConfigSchema.extend({
    salesEnabled: z.boolean().optional(),
    enabledLeadSourceTypes: z.array(z.nativeEnum(LeadSourceType)).default([]),
    crmEmail: z.string().email('CRM email must be a valid email address'),
    dealerTimezone: z.string().default('America/New_York'),
    inventoryServicesProviderIds: z.array(z.string()).default([]),
    // AI BDC assistant settings
    displayName: z.string().optional(),
    defaultLanguage: z.string().optional(),
    featuredLanguages: z.array(z.string()).optional(),
    personalityProfile: personalityProfileSchema,
    // Required with defaults — all new dealers have these populated at onboarding
    embeddedWebsiteAssistant: embeddedWebsiteAssistantSchema.default({ enabled: true }),
    sms: smsSettingsSchema.default({ enabled: false }),
    skills: skillsSchema.default({}),
    escalationEmail: z.string().email().optional(),
});
const bdcServiceConfigDtoSchema = serviceConfigSchema.extend({
    // supported defaults to true so settings-page partial updates don't need to pass it
    supported: z.boolean().default(true),
    enabledLeadSourceTypes: z.array(z.nativeEnum(LeadSourceType)).optional(),
    // crmEmail is optional in the DTO so partial updates work — Firestore merge preserves existing value
    crmEmail: z.string().email('CRM email must be a valid email address').optional(),
    inventoryServicesProviderIds: z.array(z.string()).optional(),
    // AI BDC assistant settings
    displayName: z.string().optional(),
    defaultLanguage: z.string().optional(),
    featuredLanguages: z.array(z.string()).optional(),
    personalityProfile: personalityProfileSchema,
    // Optional for partial updates — omitted fields are preserved by Firestore merge
    embeddedWebsiteAssistant: embeddedWebsiteAssistantSchema.optional(),
    sms: smsSettingsSchema.optional(),
    skills: skillsDtoSchema,
});

export { bdcServiceConfigDtoSchema, bdcServiceConfigSchema };
//# sourceMappingURL=BdcServiceConfig.js.map
